import { useEffect, useMemo, useRef, useState } from "react";
import { Clock3 } from "lucide-react";
import {
  formatSlotTime,
  formatStartTimeGroup,
  groupConsecutiveStartSlots,
} from "../lib/publicBookingSlots";
import { DateField, formatDateInputValueIT } from "./TemporalField";

const formatFirstAvailability = (slotStartTime, timeZone) => {
  const timestamp = Date.parse(slotStartTime);
  if (Number.isNaN(timestamp)) return String(slotStartTime || "");
  const date = new Date(timestamp);
  const day = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
  const time = new Intl.DateTimeFormat("it-IT", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
  return `${day} alle ${time}`;
};

export const isFiveMinuteTimeValue = (value) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(value || ""));
  return Boolean(match) && Number(match[2]) % 5 === 0;
};

const OWNER_HOURS = Array.from({ length: 24 }, (_, hour) => String(hour).padStart(2, "0"));
const OWNER_MINUTES = Array.from({ length: 12 }, (_, index) => String(index * 5).padStart(2, "0"));

export function AvailabilitySelector({
  date,
  onDateChange,
  slots = [],
  loading = false,
  error = "",
  disabledMessage = "",
  selectedStartTime = "",
  onSelectSlot,
  onFindFirstAvailability,
  schedulingMode = "",
  onSchedulingModeChange,
  slotStepMinutes = 5,
  testIdPrefix = "availability",
  dateTestId,
  minDate,
  timeZone = "Europe/Rome",
  exactTimeMode = false,
  exactTimeValue = "",
  onExactTimeChange,
  dateLabel = "Scegli il giorno",
}) {
  const [searchingFirst, setSearchingFirst] = useState(false);
  const [firstAvailabilityMessage, setFirstAvailabilityMessage] = useState("");
  const searchRequestIdRef = useRef(0);
  const activeModeRef = useRef(schedulingMode);
  const groups = useMemo(
    () => groupConsecutiveStartSlots(slots, slotStepMinutes, timeZone),
    [slots, slotStepMinutes, timeZone]
  );
  const exactTimeStepError = Boolean(exactTimeValue) && !isFiveMinuteTimeValue(exactTimeValue);
  const [exactHour = "", exactMinute = ""] = /^([0-2]\d):([0-5]\d)$/.exec(exactTimeValue)?.slice(1) || [];
  const exactHourRef = useRef(exactHour);
  const exactMinuteRef = useRef(exactMinute);
  const changeExactHour = (hour) => {
    exactHourRef.current = hour;
    const minute = OWNER_MINUTES.includes(exactMinuteRef.current) ? exactMinuteRef.current : "00";
    exactMinuteRef.current = minute;
    onExactTimeChange?.(`${hour}:${minute}`);
  };
  const changeExactMinute = (minute) => {
    exactMinuteRef.current = minute;
    const hour = OWNER_HOURS.includes(exactHourRef.current) ? exactHourRef.current : "00";
    exactHourRef.current = hour;
    onExactTimeChange?.(`${hour}:${minute}`);
  };

  useEffect(() => {
    exactHourRef.current = exactHour;
    exactMinuteRef.current = exactMinute;
  }, [exactHour, exactMinute]);

  useEffect(() => {
    activeModeRef.current = schedulingMode;
    if (schedulingMode !== "automatic") {
      searchRequestIdRef.current += 1;
      setSearchingFirst(false);
      setFirstAvailabilityMessage("");
    }
  }, [schedulingMode]);

  const handleDateChange = (event) => {
    const nextDate = event.target.value;
    if (nextDate && minDate && nextDate < minDate) {
      return;
    }
    setFirstAvailabilityMessage("");
    onDateChange(nextDate);
  };

  const handleFindFirstAvailability = async () => {
    if (!onFindFirstAvailability || searchingFirst) return;
    const requestId = searchRequestIdRef.current + 1;
    searchRequestIdRef.current = requestId;
    setSearchingFirst(true);
    setFirstAvailabilityMessage("");
    try {
      const result = await onFindFirstAvailability();
      if (searchRequestIdRef.current !== requestId || activeModeRef.current !== "automatic") return;
      const slot = result?.first_available_slot;
      if (!slot || !result?.date) {
        setFirstAvailabilityMessage("Nessuna disponibilità trovata entro 60 giorni.");
        return;
      }
      onDateChange(result.date);
      onSelectSlot(slot);
      setFirstAvailabilityMessage(`Prima disponibilità: ${formatFirstAvailability(slot.start_time, timeZone)}`);
    } catch (searchError) {
      if (searchRequestIdRef.current !== requestId || activeModeRef.current !== "automatic") return;
      setFirstAvailabilityMessage(
        searchError?.userMessage || "Non siamo riusciti a cercare la prima disponibilità."
      );
    } finally {
      if (searchRequestIdRef.current === requestId) setSearchingFirst(false);
    }
  };

  const selectSchedulingMode = (nextMode) => {
    if (nextMode !== schedulingMode) {
      searchRequestIdRef.current += 1;
      activeModeRef.current = nextMode;
      setSearchingFirst(false);
      setFirstAvailabilityMessage("");
      onSchedulingModeChange?.(nextMode);
    }
    if (nextMode === "automatic") {
      handleFindFirstAvailability();
    }
  };

  return (
    <div className="min-w-0 space-y-4" data-testid={`${testIdPrefix}-selector`}>
      <fieldset className="min-w-0 space-y-3" data-testid={`${testIdPrefix}-mode-choice`}>
        <legend className="text-base font-semibold text-slate-950">
          Quando vuoi fissare l&apos;appuntamento?
        </legend>
        <button
          type="button"
          onClick={() => selectSchedulingMode("manual")}
          className={`min-h-12 w-full rounded-sm border px-4 py-3 text-sm font-semibold ${
            schedulingMode === "manual"
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-900"
          }`}
          aria-pressed={schedulingMode === "manual"}
          data-testid={`${testIdPrefix}-mode-manual`}
        >
          Scegli giorno e orario
        </button>
        <p className="text-center text-xs font-medium uppercase tracking-wide text-slate-500">oppure</p>
        <button
          type="button"
          onClick={() => selectSchedulingMode("automatic")}
          disabled={!onFindFirstAvailability || searchingFirst}
          className={`min-h-12 w-full rounded-sm border px-4 py-3 text-sm font-semibold disabled:opacity-60 ${
            schedulingMode === "automatic"
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-900"
          }`}
          aria-pressed={schedulingMode === "automatic"}
          data-testid={`${testIdPrefix}-mode-automatic`}
        >
          {searchingFirst ? "Ricerca in corso..." : "Trova il primo appuntamento disponibile"}
        </button>
      </fieldset>

      {schedulingMode === "manual" && (
        <div className="min-w-0 space-y-4" data-testid={`${testIdPrefix}-manual-controls`}>
          <DateField
            label={dateLabel}
            value={date}
            visibleValue={formatDateInputValueIT(date)}
            visibleValueTestId={`${testIdPrefix}-date-readable`}
            onChange={handleDateChange}
            data-testid={dateTestId || `${testIdPrefix}-date`}
            required
            min={minDate}
          />

          <div className="min-w-0 rounded-sm border border-slate-200 bg-slate-50 p-3 sm:p-4">
            <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
              <Clock3 aria-hidden="true" className="h-4 w-4" />
              Orari disponibili
            </p>

            {disabledMessage && !loading && (
              <p className="mt-3 text-sm text-slate-500">{disabledMessage}</p>
            )}
            {loading && (
              <p className="mt-3 text-sm text-slate-500" data-testid={`${testIdPrefix}-slots-loading`}>
                Stiamo cercando gli orari disponibili.
              </p>
            )}
            {!loading && error && (
              <p className="mt-3 text-sm text-rose-600" data-testid={`${testIdPrefix}-slots-error`}>
                {error}
              </p>
            )}
            {!loading && !error && !disabledMessage && groups.length === 0 && (
              <p className="mt-3 text-sm text-slate-500" data-testid={`${testIdPrefix}-slots-empty`}>
                Nessun orario disponibile per questo giorno.
              </p>
            )}

            {!loading && !error && groups.length > 0 && exactTimeMode && (
              <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-exact-time-controls`}>
                <div className="space-y-2 rounded-sm border border-slate-200 bg-white p-3">
                  {groups.map((group) => (
                    <p key={group.id} className="text-sm font-medium text-slate-700">
                      {formatStartTimeGroup(group, timeZone)}
                    </p>
                  ))}
                  <p className="text-xs text-slate-500">
                    Le fasce sono una guida. L’orario scelto viene verificato alla conferma.
                  </p>
                </div>
                <fieldset className="space-y-2" data-testid={`${testIdPrefix}-exact-time`}>
                  <legend className="text-sm font-medium text-slate-700">Orario di inizio</legend>
                  <p className="text-xs text-slate-500">Intervalli disponibili: 5 minuti.</p>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-2">
                    <label className="space-y-1 text-sm">
                      <span className="block font-medium text-slate-700">Ora</span>
                      <select
                        value={OWNER_HOURS.includes(exactHour) ? exactHour : ""}
                        onChange={(event) => changeExactHour(event.target.value)}
                        className="min-h-12 w-full rounded-sm border border-slate-300 bg-white px-3 text-center text-base"
                        required
                        data-testid={`${testIdPrefix}-exact-hour`}
                      >
                        <option value="" disabled>Ora</option>
                        {OWNER_HOURS.map((hour) => <option key={hour} value={hour}>{hour}</option>)}
                      </select>
                    </label>
                    <span className="pb-3 text-lg font-semibold text-slate-700" aria-hidden="true">:</span>
                    <label className="space-y-1 text-sm">
                      <span className="block font-medium text-slate-700">Minuti</span>
                      <select
                        value={OWNER_MINUTES.includes(exactMinute) ? exactMinute : ""}
                        onChange={(event) => changeExactMinute(event.target.value)}
                        className="min-h-12 w-full rounded-sm border border-slate-300 bg-white px-3 text-center text-base"
                        required
                        data-testid={`${testIdPrefix}-exact-minute`}
                      >
                        <option value="" disabled>Minuti</option>
                        {OWNER_MINUTES.map((minute) => <option key={minute} value={minute}>{minute}</option>)}
                      </select>
                    </label>
                  </div>
                </fieldset>
                {exactTimeStepError && (
                  <p
                    id={`${testIdPrefix}-exact-time-error`}
                    className="text-sm font-medium text-rose-600"
                    role="alert"
                    data-testid={`${testIdPrefix}-exact-time-error`}
                  >
                    Scegli un orario a intervalli di 5 minuti.
                  </p>
                )}
              </div>
            )}

            {!loading && !error && groups.length > 0 && !exactTimeMode && (
              <div className="mt-3 space-y-3" data-testid={`${testIdPrefix}-slots`}>
                {groups.map((group) => (
                <div
                  key={group.id}
                  className="min-w-0 rounded-sm border border-slate-200 bg-white p-3"
                  data-testid={`${testIdPrefix}-group-${group.id}`}
                >
                  <p className="break-words text-sm font-medium text-slate-900">
                    {formatStartTimeGroup(group, timeZone)}
                  </p>
                  <label className="mt-3 block min-w-0 space-y-2 text-sm">
                    <span className="font-medium text-slate-700">Orario di inizio</span>
                    <select
                      value={group.slots.some((slot) => slot.start_time === selectedStartTime) ? selectedStartTime : ""}
                      onChange={(event) => {
                        const slot = group.slots.find((item) => item.start_time === event.target.value);
                        if (slot) onSelectSlot(slot);
                      }}
                      className="h-11 w-full min-w-0 max-w-full rounded-sm border border-slate-200 bg-white px-3 text-sm"
                      data-testid={`${testIdPrefix}-group-time-${group.id}`}
                      required
                    >
                      <option value="">Seleziona orario</option>
                      {group.slots.map((slot) => (
                        <option key={slot.start_time} value={slot.start_time}>
                          {formatSlotTime(slot.start_time, timeZone)}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {schedulingMode === "automatic" && firstAvailabilityMessage && (
        <p
          className="rounded-sm border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold text-emerald-900"
          role="status"
          data-testid={`${testIdPrefix}-first-result`}
        >
          {firstAvailabilityMessage}
        </p>
      )}
    </div>
  );
}
